import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { getInformantDashboardPath, getPatientDashboardPath } from '../utils/informantDashboardRouting';

/**
 * AutoLoginRedirect Component
 * 
 * This component handles automatic redirection of already-authenticated users
 * who visit the home page or login pages. Instead of showing the login form,
 * it redirects them to their appropriate dashboard.
 */
// After sign-in, Firebase custom claims can lag behind the auth token
// by a few seconds (the Cloud Function that sets them may not have
// finished). We retry a token refresh up to this many times before
// giving up and falling through to the login page.
const MAX_CLAIMS_RETRIES = 2;
const CLAIMS_RETRY_DELAY_MS = 1500;

export function AutoLoginRedirect({ children, fallbackRoute = '/login' }) {
  const { 
    isAuthenticated, 
    isLoading, 
    claims, 
    isEmailVerified, 
    isMFAEnrolled,
    hasSetOwnPassword,
    user,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect while still loading auth state
    if (isLoading) return;

    // If not authenticated, show the login page
    if (!isAuthenticated) return;

    // console.log('[TD-SSO] AutoLoginRedirect: authenticated, determining redirect destination', {
    //   pathname: typeof window !== 'undefined' ? window.location.pathname : '',
    //   accountType: claims?.accountType,
    //   userType: claims?.userType,
    //   authMode: claims?.authMode,
    //   hasSetOwnPassword,
    //   isEmailVerified,
    //   isMFAEnrolled,
    // });

    // If user is authenticated, determine where to redirect based on their state and type
    const redirectUser = async () => {
      try {
        const accountType = claims?.accountType;
        const userType = claims?.userType;

        console.log('[TD-SSO] AutoLoginRedirect: claims snapshot', { accountType, userType, hasSetOwnPassword, authMode: claims?.authMode });

        // ------------------------------------------------------------------
        // Microsoft-first invite onboarding (claims may be missing temporarily)
        // ------------------------------------------------------------------
        //
        // When a user signs in with Microsoft for the first time under the invite-based model:
        // - Firebase Auth creates the user with providerId=microsoft.com
        // - There are no ThinkDivergent custom claims yet (accountType/userType/rmsOrgId)
        // - There is no provider profile document yet (clinicians/{uid} for Assessment,
        //   rms_organisations/{orgId}/rmsProviderUsers/{RPU-xxx} for RMS)
        //
        // We must NOT redirect them back to /login due to missing claims.
        // Instead, we attempt to consume the invite (server-side) and then reload
        // so new custom claims are applied and normal routing proceeds.
        if (!accountType && user) {
          const providerIds = (user.providerData || []).map(p => p?.providerId).filter(Boolean);
          const isMicrosoftProvider = providerIds.includes('microsoft.com');
          if (isMicrosoftProvider) {
            try {
              console.log('[TD-SSO] AutoLoginRedirect: claims missing for Microsoft user; attempting consumeProviderUserInvite', {
                uid: user.uid,
                providerIds,
              });
              const consumeInvite = httpsCallable(functions, 'consumeProviderUserInvite');
              await consumeInvite();
              // Force refresh token so new custom claims are visible
              await user.getIdTokenResult(true);
              console.log('[TD-SSO] AutoLoginRedirect: invite consumed; reloading to apply claims');
              // Full reload guarantees the app re-evaluates claims + guards from a clean state.
              window.location.assign('/dashboard');
              return;
            } catch (e) {
              console.warn('[TD-SSO] AutoLoginRedirect: consumeProviderUserInvite failed', e?.code, e?.message);
              // Fail closed: sign out to avoid leaving the app in an "authenticated but unusable" state.
              try {
                // eslint-disable-next-line no-undef
                const { signOut } = await import('firebase/auth');
                await signOut((await import('../firebase')).auth);
              } catch (_) {}
              // Surface a stable hint for the login screen.
              try {
                const url = new URL(window.location.href);
                url.pathname = fallbackRoute;
                url.searchParams.set('sso_error', String(e?.message || 'invite_not_found'));
                window.location.assign(url.toString());
                return;
              } catch (_) {}
              navigate(fallbackRoute);
              return;
            }
          }
        }

        // Handle password setup redirect.
        //
        // Microsoft-first accounts (authMode='with_microsoft_sso') intentionally do NOT require
        // the user to set a ThinkDivergent password during pilots. A password exists only as a
        // break-glass fallback and is not part of the normal user journey.
        //
        // RMS Service Users use passwordless magic links and never go through
        // manual password setup, so they are excluded from this gate entirely.
        const isMicrosoftFirstAccount = claims?.authMode === 'with_microsoft_sso';
        const isRmsServiceUser = accountType === 'RMS Service User';
        if (!hasSetOwnPassword && !isMicrosoftFirstAccount && !isRmsServiceUser) {
          console.log('[TD-SSO] AutoLoginRedirect: redirecting to password setup', { accountType, isMicrosoftFirstAccount });
          // Base64-encode then percent-encode the email for the URL query
          // string. Standard base64 can produce +, / and = which have special
          // meaning in URLs — encodeURIComponent ensures they are escaped so
          // URLSearchParams.get('e') on the receiving page recovers the
          // original base64 for atob().
          const email = encodeURIComponent(btoa(claims?.email || ''));
          if (accountType === 'Service User') {
            navigate(`/service-user/set-up-password?setup=true&e=${email}`);
          } else if (accountType === 'RMS Provider User') {
            navigate(`/rms/provider/set-up-password?setup=true&e=${email}`);
          } else {
            navigate(`/set-up-password?setup=true&e=${email}`);
          }
          return;
        }

        // Handle Provider (Clinician/Admin) redirects
        if (accountType === 'Provider') {
          // Check email verification first
          if (!isEmailVerified) {
            console.log('[TD-SSO] AutoLoginRedirect: Provider email not verified -> /verify-email');
            navigate('/verify-email');
            return;
          }

          // Check MFA enrollment (method-agnostic: covers both SMS and TOTP/authenticator app).
          // For Microsoft-first accounts, we skip Firebase MFA because Entra typically enforces MFA.
          if (!isMicrosoftFirstAccount && !isMFAEnrolled) {
            console.log('[TD-SSO] AutoLoginRedirect: Provider MFA not enrolled -> /set-up-mfa', { isMicrosoftFirstAccount });
            navigate('/set-up-mfa');
            return;
          }

          // Microsoft-first accounts:
          // If the backend still marks setup incomplete (legacy hasSetOwnPassword=false),
          // finalize setup now so downstream logic is consistent.
          if (isMicrosoftFirstAccount && !hasSetOwnPassword) {
            try {
              console.log('[TD-SSO] AutoLoginRedirect: completing Microsoft-first setup via callable completeProviderUserSSOSetup');
              const completeSSO = httpsCallable(functions, 'completeProviderUserSSOSetup');
              await completeSSO();
              console.log('[TD-SSO] AutoLoginRedirect: completeProviderUserSSOSetup success');
            } catch (e) {
              // If already completed, backend returns FAILED_PRECONDITION; treat as ok.
              console.warn('[TD-SSO] AutoLoginRedirect: completeProviderUserSSOSetup failed:', e?.code, e?.message);
            }
          }

          // All checks passed - route to ADHD Assessment dashboard
          console.log('[TD-SSO] AutoLoginRedirect: Provider fully set up -> /dashboard', { isMicrosoftFirstAccount, userType });
          navigate('/dashboard');
          return;
        }

        // Handle RMS Provider User redirects
        if (accountType === 'RMS Provider User') {
          if (!isEmailVerified) {
            navigate('/verify-email');
            return;
          }
          if (!isMicrosoftFirstAccount && !isMFAEnrolled) {
            navigate('/set-up-mfa');
            return;
          }
          console.log('[TD-SSO] AutoLoginRedirect: RMS Provider fully set up -> /rms/provider/dashboard', { userType });
          navigate('/rms/provider/dashboard');
          return;
        }

        // Handle Service User (Patient/Informant) redirects
        if (accountType === 'Service User') {
          // Check MFA enrollment for service users
          if (!isMFAEnrolled) {
            console.log('[TD-SSO] AutoLoginRedirect: Service user MFA not enrolled -> /service-user/set-up-mfa');
            navigate('/service-user/set-up-mfa');
            return;
          }

          // Check if it's first login (welcome screen)
          // We'll need to fetch user data to check hasSeenWelcome
          // For now, redirect based on user type
          if (userType === 'Informant') {
            const informantPath = getInformantDashboardPath(claims);
            console.log(`[TD-SSO] AutoLoginRedirect: Informant -> ${informantPath}`);
            navigate(informantPath);
          } else {
            const patientPath = getPatientDashboardPath(claims);
            console.log(`[TD-SSO] AutoLoginRedirect: Patient -> ${patientPath}`);
            navigate(patientPath);
          }
          return;
        }

        // Handle RMS Service User redirects
        if (accountType === 'RMS Service User') {
          console.log('[TD-SSO] AutoLoginRedirect: RMS Service User -> /rms/dashboard', { userType });
          navigate('/rms/dashboard');
          return;
        }

        // ──────────────────────────────────────────────────────────────
        // Transient null-claims retry
        // ──────────────────────────────────────────────────────────────
        // Custom claims can lag behind Firebase Auth by a few seconds
        // after sign-in (token issued before the Cloud Function that
        // sets claims finishes). Rather than bouncing the user straight
        // back to /login — which creates a confusing sign-in loop — we
        // wait briefly and force a token refresh. If claims arrive
        // during the retry window, we return and let the useEffect
        // re-fire with the updated claims (onIdTokenChanged propagates
        // them into useAuth's state). If they never arrive, we fall
        // through to the login page.
        if (!accountType && user) {
          let refreshedAccountType = null;
          for (let attempt = 0; attempt < MAX_CLAIMS_RETRIES; attempt++) {
            console.log('[TD-SSO] AutoLoginRedirect: claims missing, retrying after short wait', {
              attempt: attempt + 1,
              maxRetries: MAX_CLAIMS_RETRIES,
            });
            await new Promise(r => setTimeout(r, CLAIMS_RETRY_DELAY_MS));
            try {
              const refreshed = await user.getIdTokenResult(true);
              refreshedAccountType = refreshed?.claims?.accountType;
              if (refreshedAccountType) break;
            } catch (_) {}
          }

          if (refreshedAccountType) {
            // Claims propagated — the token refresh triggered
            // onIdTokenChanged in useAuth, so this useEffect will
            // re-run with the correct claims momentarily.
            return;
          }
        }

        // Fallback if we can't determine user type after retries
        console.log('[TD-SSO] AutoLoginRedirect: could not determine user type after retries, using fallback route', { fallbackRoute });
        navigate(fallbackRoute);

      } catch (error) {
        console.error('[TD-SSO] AutoLoginRedirect: error during redirect logic:', error);
        // On error, don't redirect - let user see the login page
      }
    };

    redirectUser();
  }, [isAuthenticated, isLoading, claims, isEmailVerified, isMFAEnrolled, hasSetOwnPassword, user, navigate, fallbackRoute]);

  // Show loading spinner while determining auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is authenticated, don't render children (they'll be redirected).
  // Show a context-aware message: "Setting up your account..." while claims
  // are still propagating, "Redirecting..." once claims are present.
  if (isAuthenticated) {
    const spinnerText = claims?.accountType ? 'Redirecting...' : 'Setting up your account...';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{spinnerText}</p>
        </div>
      </div>
    );
  }

  // If not authenticated, render the login component
  return children;
} 